import { Space } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import usePermissionsByCompetitionName from "~hooks/usePermissionsByCompetitionName";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useSeasonEntityService } from "~pages/SeasonEntities/hooks/useSeasonEntityService.ts";
import { AddEntityToSeasonModal } from "~pages/SeasonEntities/SeasonEntityListPage/components/AddEntityToSeasonModal.tsx";
import {
  defaultSeasonEntityExportFieldNames,
  seasonEntityExportFields,
} from "~pages/SeasonEntities/SeasonEntityListPage/constants/exportFields.ts";
import { SeasonEntityTableFilters } from "~pages/SeasonEntities/SeasonEntityListPage/enums/seasonEntityTableFilters.ts";
import { useFetchSeasonEntities } from "~pages/SeasonEntities/SeasonEntityListPage/hooks/useFetchSeasonEntities.ts";
import { useGetSeasonEntityTableColumns } from "~pages/SeasonEntities/SeasonEntityListPage/hooks/useGetSeasonEntityTableColumns.tsx";
import { useGetSeasonEntityTableFilters } from "~pages/SeasonEntities/SeasonEntityListPage/hooks/useGetSeasonEntityTableFilters.tsx";

const DEFAULT_SORTER = { columnKey: "nameFullLocal", order: "ascend" as const };

const SeasonEntityListPage = () => {
  const { sport, customerId, organizationId, seasonId } = useParams();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const paginationConfig = useHandlePagination(20);
  const [searchText, setSearchText] = useState("");
  const [massEditMode, setMassEditMode] = useState(false);
  const [isAddEntityToSeasonModalOpen, setIsAddEntityToSeasonModalOpen] = useState(false);
  const { seasonEntityPermissionName } = usePermissionsByCompetitionName();
  const seasonEntityPermission = seasonEntityPermissionName as PermissionNames;
  const { hasPermission } = useUserPermissions();
  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.seasonEntities.listPage;
  const dataTableImportConfiguration = uiConfiguration.pages.seasonEntities.listPage.importConfig;
  const columns = useGetSeasonEntityTableColumns(massEditMode);
  const { currentFilter, filters, handleFilterChange } = useGetSeasonEntityTableFilters();
  const { importSeasonEntities, exportSeasonEntities, isExportPending, deleteSeasonEntity, isDeletePending } = useSeasonEntityService();
  const [sortBy, setSortBy] = useState("nameFullLocal");

  const handleSearch = useCallback(
    (value: string) => {
      paginationConfig.resetPagination();
      setSearchText(value);
    },
    [paginationConfig],
  );

  const handleFiltersChange = useCallback(
    (updatedFilter: Record<string, unknown>) => {
      paginationConfig.resetPagination();
      handleFilterChange(updatedFilter);
    },
    [handleFilterChange, paginationConfig],
  );

  const listParams = useMemo(
    () => ({
      include: "entities,conferences,divisions",
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      entityNameFullLocalContains: searchText,
      gender: currentFilter[SeasonEntityTableFilters.Gender]?.value,
      ageGroup: currentFilter[SeasonEntityTableFilters.AgeGroup]?.value,
      standard: currentFilter[SeasonEntityTableFilters.Standard]?.value,
      discipline: currentFilter[SeasonEntityTableFilters.Discipline]?.value,
      status: currentFilter[SeasonEntityTableFilters.Status]?.value,
      sortBy,
    }),
    [currentFilter, paginationConfig.offset, paginationConfig.pageSize, searchText, sortBy],
  );

  const { data, refetch, isPending } = useFetchSeasonEntities({
    customParams: listParams,
  });
  const seasonEntityIdSet = useMemo(() => new Set(data?.data?.map(({ entityId }) => entityId) || []), [data]);

  const getNavigateUrl = (entityId: string, path = "") =>
    `/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/entities/${entityId}${path}`;

  const canReadRoster = hasPermission(PermissionNames.SEASON_ROSTER, PermissionOperations.READ);
  const canReadStaff = hasPermission(PermissionNames.ROLES, PermissionOperations.READ);
  const onMassEditModeChangedHandler = useCallback((checked: boolean) => {
    setMassEditMode(checked);
  }, []);

  return (
    <>
      <AddEntityToSeasonModal
        isOpen={isAddEntityToSeasonModalOpen}
        setIsOpen={setIsAddEntityToSeasonModalOpen}
        reloadData={refetch}
        existingSeasonEntityIdSet={seasonEntityIdSet}
      />
      <DataTable
        handleRefresh={async () => {
          await refetch();
        }}
        title={formatMessage({ id: "entities" })}
        columns={columns}
        rowActions={{
          custom: {
            roster: {
              hidden: () => !canReadRoster,
              renderAction: (row) => (
                <Space onClick={() => navigate(getNavigateUrl(row.entityId, "/roster"))}>
                  <i className="fas fa-users mr-2" />
                  <span>{formatMessage({ id: "roster" })}</span>
                </Space>
              ),
            },
            staff: {
              hidden: () => !canReadStaff,
              renderAction: (row) => (
                <Space onClick={() => navigate(getNavigateUrl(row.entityId, "/staff"))}>
                  <i className="fas fa-user-tie mr-2" />
                  <span>{formatMessage({ id: "staff" })}</span>
                </Space>
              ),
            },
          },
          base: {
            manage: {
              onClick: (row) => navigate(getNavigateUrl(row.entityId)),
            },
            delete: {
              onClick: (row) => {
                void deleteSeasonEntity(row.entityId);
              },
              itemName: (row) => row.entity?.nameFullLocal || row.entityId,
              loading: isDeletePending,
            },
          },
        }}
        rowNavigation={{
          getNavigateUrl: (row) => getNavigateUrl(row.entityId),
        }}
        rowKey={"entityId"}
        rowNameKey={"entityId"}
        dataSource={data?.data || []}
        columnChooserLocalStorageKey={`${sport}.tables.settings.columns.seasonEntities`}
        isDataLoading={isPending}
        handleAdd={() => setIsAddEntityToSeasonModalOpen(true)}
        addButtonIntlKey={"entity.add"}
        filtersConfig={{
          currentFilter,
          filters,
          handleFilterChange: handleFiltersChange,
        }}
        searchConfig={{
          handleSearch: handleSearch,
          searchText: searchText,
        }}
        columnSortConfig={{
          sorterLocalStorageKey: `${sport}.tables.settings.sort.seasonEntities`,
          defaultSorter: DEFAULT_SORTER,
          onSortChange: setSortBy,
        }}
        onMassEditModeChangedHandler={onMassEditModeChangedHandler}
        massEditMode={massEditMode}
        exportConfig={{
          handleExport: exportSeasonEntities,
          loading: isExportPending,
          defaultFields: defaultSeasonEntityExportFieldNames,
          groupedFields: seasonEntityExportFields,
          localStorageKey: "export.seasonEntities",
        }}
        importConfig={{
          ...dataTableImportConfiguration,
          handleImport: importSeasonEntities,
        }}
        permissionNames={[seasonEntityPermission]}
        dataTableUiConfiguration={dataTableUiConfiguration}
        paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
      />
    </>
  );
};

export default SeasonEntityListPage;
